import { QuestionReviewStatusTag } from '@exam-management/components';
import type { SearchOwnedAndAssignedTasksQuery } from '@exam-management/graphql';
import { UserTypeAvatar } from '@exam-management/user-management';
import { EllipsisText, Flex, Text, type GridColDef } from '@shared/components';
import { formatDate, formatDateTime } from '@shared/utils';

export type QuestionReviewTask = Pick<
  SearchOwnedAndAssignedTasksQuery['wflSearchOwnedAndAssignedTasks']['tasks'][number],
  | 'dueDate'
  | 'name'
  | 'status'
  | 'code'
  | 'id'
  | 'assignee'
  | 'assigner'
  | 'updatedAt'
  | 'parentTask'
  | 'currentWorkflowStep'
  | 'metadata'
  | 'subtasks'
>;

export const columns: GridColDef<QuestionReviewTask>[] = [
  {
    field: 'code',
    headerName: 'Task ID',
    width: 90,
    sortable: false,
    renderCell: (params) => {
      return (
        <EllipsisText variant='inherit' sx={{ width: 1 }}>
          {params.row.code}
        </EllipsisText>
      );
    },
  },
  {
    field: 'name',
    headerName: 'Task Name',
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      return (
        <EllipsisText variant='inherit' sx={{ width: 1 }}>
          {params.row.name ?? params.row.parentTask?.name}
        </EllipsisText>
      );
    },
  },
  {
    field: 'subtaskCount',
    headerName: 'Qty',
    width: 50,
    sortable: false,
    renderCell: (params) => {
      if (params.row.metadata.__typename === 'TaskMetadataWithReviewQuestion') {
        return <Text variant='body2'>{params.row.metadata.questionQuantity}</Text>;
      }
    },
  },
  {
    field: 'qBankCode',
    headerName: 'Course ID',
    width: 120,
    sortable: false,
    renderCell: (params) => {
      if (params.row.metadata.__typename === 'TaskMetadataWithReviewQuestion') {
        return <Text variant='body2'>{params.row.metadata.qBankStructure?.code ?? '-'}</Text>;
      }
    },
  },
  {
    field: 'assigner',
    headerName: 'Assigned by',
    sortable: false,
    width: 200,
    renderCell: (params) => {
      const fullName = `${params.row.assigner.firstname} ${params.row.assigner.lastname}`;
      return (
        <Flex sx={{ gap: 2, alignItems: 'center' }}>
          <UserTypeAvatar
            fullName={fullName}
            sx={{ width: 32, height: 32 }}
            email={params.row.assigner.email}
          />
          <EllipsisText variant='inherit'>{fullName}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'assignee',
    headerName: 'Assignee',
    sortable: false,
    width: 200,
    renderCell: (params) => {
      const subtask = params.row.subtasks[0];
      if (!subtask) {
        return '-';
      }
      const fullName = `${subtask.assignee.firstname} ${subtask.assignee.lastname}`;

      return (
        <Flex sx={{ gap: 2, alignItems: 'center' }}>
          <UserTypeAvatar
            fullName={fullName}
            sx={{ width: 32, height: 32 }}
            email={subtask.assignee.email}
          />
          <EllipsisText variant='inherit'>{fullName}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    width: 120,
    sortable: false,
    renderCell: (params) => {
      const subtask = params.row.subtasks[0];
      return <Text variant='body2'>{subtask?.dueDate ? formatDate(subtask.dueDate) : '-'}</Text>;
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Last Modified',
    width: 140,
    sortable: true,
    renderCell: (params) => {
      return <Text variant='body2'>{formatDateTime(params.row.updatedAt)}</Text>;
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    sortable: false,
    renderCell: (params) => {
      return <QuestionReviewStatusTag status={params.row.currentWorkflowStep.code} />;
    },
  },
];
